import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Signup.css';
import { getApiUrl } from '../config/api';

function formatPhoneDisplay(digits) {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function parsePhoneDigits(formatted) {
  return formatted.replace(/\D/g, '').slice(0, 10);
}

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState('');

  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    phone: '',
    email: '',
    password: '',
    confirm_password: '',
    sms_consent_transactional: false,
    sms_consent_proxy_rental: false
  });
  const [message, setMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (!inviteToken) return;

    const verifyInvite = async () => {
      try {
        const res = await fetch(
          getApiUrl(`/api/v1/organization/invite/verify?token=${encodeURIComponent(inviteToken)}`)
        );
        if (res.ok) {
          const data = await res.json();
          setInviteInfo(data);
          setFormData((prev) => ({ ...prev, email: data.email }));
        } else {
          const data = await res.json();
          setInviteError(data.error || 'Invalid or expired invitation link.');
        }
      } catch (err) {
        console.error('Error verifying invite:', err);
        setInviteError('Unable to verify invitation. Please try again.');
      } finally {
        setInviteLoading(false);
      }
    };

    verifyInvite();
  }, [inviteToken]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validatePasswords = () => {
    if (formData.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setPasswordError("Passwords do not match");
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    if ((formData.sms_consent_transactional || formData.sms_consent_proxy_rental) && !formData.phone.trim()) {
      setPhoneError('Phone number is required to enable SMS.');
      return;
    }
    setPhoneError('');

    if (inviteToken && inviteInfo) {
      await handleInviteJoin();
    } else {
      await handleOrgCreate();
    }
  };

  const handleInviteJoin = async () => {
    const submitData = {
      invite_token: inviteToken,
      first_name: formData.first_name,
      last_name: formData.last_name,
      password: formData.password,
      sms_consent_transactional: formData.sms_consent_transactional,
      sms_consent_proxy_rental: formData.sms_consent_proxy_rental,
    };
    if (formData.phone) {
      submitData.phone = `+1${formData.phone}`;
    }

    try {
      const response = await fetch(getApiUrl('/api/v1/organization/join'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to join organization.');
        return;
      }

      const data = await response.json();

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user_id', data.user_id);
      localStorage.setItem('first_name', data.first_name);

      setSuccess(true);
      setMessage('');
      setTimeout(() => { navigate('/dashboard'); }, 4000);
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred. Please try again.');
    }
  };

  const handleOrgCreate = async () => {
    const submitData = { ...formData };
    delete submitData.confirm_password;
    if (submitData.phone) {
      submitData.phone = `+1${submitData.phone}`;
    }

    try {
      const response = await fetch(getApiUrl('/api/v1/organization/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to create organization.');
        return;
      }

      const loginResponse = await fetch(getApiUrl('/api/v1/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      if (loginResponse.ok) {
        const loginData = await loginResponse.json();
        
        localStorage.setItem('access_token', loginData.access_token);
        localStorage.setItem('refresh_token', loginData.refresh_token);
        localStorage.setItem('user_id', loginData.user_id);
        localStorage.setItem('first_name', loginData.first_name);
        
        setSuccess(true);
        setMessage('');
        setTimeout(() => { navigate('/dashboard'); }, 4000);
      } else {
        setMessage('Account created! Please log in.');
        setTimeout(() => { navigate('/login'); }, 1500);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred. Please try again.');
    }
  };

  if (inviteLoading) {
    return (
      <div className="page-container">
        <div className="signup-wrapper">
          <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
          <div className="signup-box">
            <p className="text-center text-gray-600 py-8">Verifying invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (inviteToken && inviteError) {
    return (
      <div className="page-container">
        <div className="signup-wrapper">
          <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
          <div className="signup-box">
            <div className="text-center py-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Invalid Invitation</h2>
              <p className="text-gray-600">{inviteError}</p>
              <button
                className="mt-6 px-6 py-2 bg-green-700 text-white rounded hover:bg-green-800"
                onClick={() => navigate('/signup')}
              >
                Create a new account instead
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isInviteFlow = inviteToken && inviteInfo;
  const roleDisplay = inviteInfo?.role === 'org_admin' ? 'Administrator' : 'Landlord';

  return (
    <div className="page-container">
      <div className="signup-wrapper">
        <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
        <div className="signup-box">
          {!success ? (
            <form onSubmit={handleSubmit}>
              {isInviteFlow ? (
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Join {inviteInfo.org_name}</h2>
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                    You've been invited to join <strong>{inviteInfo.org_name}</strong> as
                    a <strong>{roleDisplay}</strong>. Fill in your details below to create your account.
                  </div>
                </div>
              ) : (
                <h2 className="text-xl font-semibold text-gray-800">Create an account</h2>
              )}
              <div className="signup-name-row">
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.first_name}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
              {!isInviteFlow && (
                <input
                  type="text"
                  name="company_name"
                  placeholder="Company Name (optional)"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.company_name}
                  onChange={handleChange}
                />
              )}
              <input
                type="email"
                name="email"
                placeholder="email@domain.com"
                className={`mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400 ${
                  isInviteFlow ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
                value={formData.email}
                onChange={handleChange}
                readOnly={isInviteFlow}
              />
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  value="+1"
                  readOnly
                  className="w-[50px] p-2 border rounded bg-gray-100 text-gray-500 text-center cursor-not-allowed"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="(555) 000-0000"
                  className="flex-1 p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formatPhoneDisplay(formData.phone)}
                  onChange={(e) => setFormData({ ...formData, phone: parsePhoneDigits(e.target.value) })}
                />
              </div>
              {phoneError && <p className="error-message">{phoneError}</p>}
              <input
                type="password"
                name="password"
                placeholder="Password (min 8 characters)"
                className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                value={formData.password}
                onChange={handleChange}
              />
              <input
                type="password"
                name="confirm_password"
                placeholder="Confirm Password"
                className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                value={formData.confirm_password}
                onChange={handleChange}
              />
              {passwordError && <p className="error-message">{passwordError}</p>}
              <div className="sms-consent-section">
                <div className="sms-consent-container">
                  <label className="sms-consent-label">
                    <input
                      type="checkbox"
                      name="sms_consent_transactional"
                      checked={formData.sms_consent_transactional}
                      onChange={handleChange}
                      className="sms-consent-checkbox"
                    />
                    <span className="sms-consent-text">
                      I agree to receive SMS from ZenRent for account and transactional notifications (e.g., verification, security alerts, important account updates). Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See{' '}
                      <a href="https://zenrent.net/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> &amp;{' '}
                      <a href="https://zenrent.net/terms" target="_blank" rel="noopener noreferrer">Terms</a>. Consent is not a condition of purchase.
                    </span>
                  </label>
                </div>
                <div className="sms-consent-container">
                  <label className="sms-consent-label">
                    <input
                      type="checkbox"
                      name="sms_consent_proxy_rental"
                      checked={formData.sms_consent_proxy_rental}
                      onChange={handleChange}
                      className="sms-consent-checkbox"
                    />
                    <span className="sms-consent-text">
                      I agree to send and receive SMS via ZenRent for rental communications, including messages exchanged with my landlord/tenant through ZenRent using proxy phone numbers (e.g., maintenance updates, scheduling, rental coordination). Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See{' '}
                      <a href="https://zenrent.net/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> &amp;{' '}
                      <a href="https://zenrent.net/terms" target="_blank" rel="noopener noreferrer">Terms</a>. Consent is not a condition of purchase.
                    </span>
                  </label>
                </div>
              </div>
              <button
                type="submit"
                className="mt-4 w-full p-2 bg-green-700 text-white rounded hover:bg-green-800"
              >{isInviteFlow ? 'Join Organization' : 'Sign Up'}</button>
              <p className="mt-4 text-center text-gray-600">
                Already have an account? <span
                  className="text-green-700 cursor-pointer hover:underline"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </span>
              </p>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">✓</div>
              <h2 className="text-xl font-semibold text-green-700">
                {isInviteFlow ? 'Welcome to the team!' : 'Account Created!'}
              </h2>
              <p className="text-gray-600 mt-2">Redirecting to your dashboard...</p>
            </div>
          )}
        </div>
        {message && !success && <p className={message.includes('successfully') ? 'success-message' : 'error-message'}>{message}</p>}
      </div>
    </div>
  );
};

export default Signup;
